// LPA Price Calculator data — extracted from LPA_Price_Calculator_14.xlsx (Data tab).

export const EXCHANGE_RATES = { AUD: 1, USD: 0.63, EUR: 0.53, HKD: 5.06, GBP: 0.45 }; // 1 AUD = X

export const DEFAULT_FREIGHT = { sea: 0.20, air: 0.35 };

// Supplier -> { sea, air } freight percentages
export const SUPPLIER_FREIGHT = {
  'Yisen': { sea: 0.10, air: 0.31 },
  'AGC': { sea: 0.28, air: 0.70 },
  'AGC Lighting': { sea: 0.28, air: 0.70 },
  'DHM': { sea: 0.49, air: 1.25 },
  'Brightmind': { sea: 0.12, air: 0.60 },
  'Upshine': { sea: 0.25, air: 0.48 },
  'Thinkmax': { sea: 0.16, air: 0.63 },
  'Gleming': { sea: 0.14, air: 0.39 },
  'Unilamp': { sea: 0.28, air: 0.55 },
  'Lumbency': { sea: 0.11, air: 0.76 },
  'Dilux': { sea: 0.10, air: 0.28 },
  'Visa Lighting': { sea: 0.06, air: 0.66 },
  'Canco': { sea: 0.30, air: 0.65 },
  'Yaham': { sea: 0.16, air: 0.70 },
  'Waytop': { sea: 0.25, air: 0.96 },
  'WK': { sea: 0.25, air: 0.33 },
  'Leo': { sea: 0.33, air: 0.43 },
  'Ewin Lighting': { sea: 0.45, air: 0.65 },
  'Tons /Titan': { sea: 0.10, air: 0.43 },
  'New Shine': { sea: 0.21, air: 0.40 },
  'E-lite': { sea: 0.10, air: 0.31 },
  'YuanHui': { sea: 0.15, air: 0.35 },
  'Lightnet': { sea: 0.16, air: 0.40 },
  'Landa': { sea: 0.50, air: 0.35 },
  'Linea Light': { sea: 0.18, air: 0.45 },
  'Pan/Sibilla': { sea: 0.50, air: 0.35 },
  'Egoluce': { sea: 0.20, air: 0.40 },
  'PCD Lighting': { sea: 0.10, air: 0.31 },
  'DAL': { sea: 0.25, air: 0.50 },
};

export const SUPPLIERS = Object.keys(SUPPLIER_FREIGHT).sort();
export const DEFAULT_SUPPLIER = 'YuanHui';

// NetSuite vendor names carry an account number and legal suffixes
// ("2001 AGC Lighting Co., Ltd"), so map them back onto a calculator supplier.
const normSup = s => (s || '').toUpperCase().replace(/^\s*\d+\s+/, '').replace(/[^A-Z0-9]+/g, ' ').trim();
// Words that say nothing about which supplier it is.
const SUP_STOP = new Set(['LIGHTING', 'LIGHT', 'LIGHTS', 'LIGHTINGS', 'CO', 'LTD', 'INC', 'LIMITED', 'PTY',
  'GROUP', 'TECHNOLOGY', 'TECHNOLOGIES', 'ELECTRIC', 'ELECTRICAL', 'ELECTRONICS', 'OPTOELECTRONICS',
  'PHOTOELECTRIC', 'INTERNATIONAL', 'AUSTRALIA', 'GMBH', 'SRL', 'SPA', 'THE', 'AND', 'OF']);

export function matchSupplier(vendorName) {
  const v = normSup(vendorName);
  if (!v) return null;
  const words = v.split(' ');
  let best = null;
  SUPPLIERS.forEach(key => {
    const k = normSup(key);
    if (!k) return;
    const distinct = k.split(' ').filter(w => w.length >= 3 && !SUP_STOP.has(w));
    const longest = ws => Math.max(...ws.map(w => w.length));
    let score = 0;
    if (k === v) score = 1000;
    else if ((' ' + v + ' ').includes(' ' + k + ' ')) score = 600 + k.length;
    else if (distinct.length && distinct.every(w => words.includes(w))) score = 500 + k.length;
    else {
      // Either half of a two-brand supplier (Tons /Titan, Pan/Sibilla).
      const whole = distinct.filter(w => w.length >= 4 && words.includes(w));
      if (whole.length) score = 400 + longest(whole);
      else {
        // Brand glued to a word: "Ewinlight" -> Ewin. Needs a decent remainder
        // so near-misses (Linear vs Linea) do not match.
        const glued = distinct.filter(w => w.length >= 4
          && words.some(x => x.startsWith(w) && x.length - w.length >= 2));
        if (glued.length) score = 300 + longest(glued);
      }
    }
    if (score && (!best || score > best.score)) best = { key, score };
  });
  return best ? best.key : null;
}

// Mark-up tiers: sell price = landed AUD / divisor
export const MARKUP_TIERS = [
  { key: 'project', label: 'Project', divisor: 0.55 },
  { key: 'wholesale', label: 'Wholesale', divisor: 0.50 },
  // Wholesale plus 5% — the default quoting tier, so it sits in the calc table too.
  { key: 'wholesale5', label: 'Wholesale +5%', divisor: 0.50 / 1.05 },
  { key: 'trade', label: 'Trade', divisor: 0.40 },
  { key: 'retail', label: 'Retail', divisor: 0.35 },
  { key: 'enduser', label: 'End User', divisor: 0.30 },
];

// EU trade-catalogue discounts (auto-applied by supplier)
export const EU_TRADE_DISCOUNTS = {
  'Landa': 0.55,
  'Pan/Sibilla': 0.60,
  'Egoluce': 0.50,
  'Lightnet': 0.55,
  'Linea Light': 0.50,
};

export function calcRow({ cost, originCur, supplier, freightType, euDiscount, customFreight }) {
  const c = Number(cost);
  if (!c || c <= 0) return null;
  const disc = Number(euDiscount) || 0;
  const net = c * (1 - disc);
  let freight;
  if (freightType === 'custom') {
    const cf = parseFloat(customFreight);
    freight = isNaN(cf) ? 0 : cf / 100;
  } else {
    const sf = SUPPLIER_FREIGHT[supplier];
    freight = sf ? sf[freightType] : DEFAULT_FREIGHT[freightType];
  }
  const rate = EXCHANGE_RATES[originCur];
  const landedAud = (net / rate) * (1 + freight);
  const tiers = {};
  MARKUP_TIERS.forEach(t => { tiers[t.key] = landedAud / t.divisor; });
  return { net, freight, landedAud, tiers };
}

export function toDisplay(aud, displayCur) {
  return aud * EXCHANGE_RATES[displayCur];
}
